import {
    RiDeleteBinLine, RiEdit2Line, RiFileCopyLine, RiFileList2Line, RiFlashlightLine,
    RiLayout6Line,
} from "@remixicon/react";
import React, {
    ReactNode,
    useCallback,
    useMemo,
} from "react";
import {
    Form,
    FormType,
    GetFormDataDocument,
    GetFormDocument,
    GetRequestTypesDocument,
    useAttachWorkflowMutation,
    useCloneFormMutation,
    useDeleteFormMutation,
    useGetRequestTypesQuery,
    UserGroup,
    useSaveFormMutation,
    useSaveWorkflowMutation,
    WorkflowTriggerType,
} from "../../../../__generated__/api";
import useRouting from "../../../../contexts/Routing";

import { ReactComponent as GroupsList } from "../../../../assets/icons/groups-list.svg";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import Badge from "../../../../components/theme/badge/Badge";
import SettingsContainer from "../../../../components/theme/settings";
import createTableContainer from "../../../../components/theme/table";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import { TemplateItem, useTemplate } from "../../../../components/template/Template";
import ROLES, { ROLE_MAP } from "../../../../job-function-roles";
import { HtmlRenderer } from "../../../../components/html-render/HtmlRenderer";

export const ICON_MAP = ROLES.toIdMap("title", item => <item.icon />);

const DEFAULT_NAME = "Request Type";
const DEFAULT_DESCRIPTION = "Triggered when a user creates a ticket after filling out a request form.";

interface FormWithGroups extends Form {
    groups: UserGroup[]
}
const TableContainer = createTableContainer<FormWithGroups>();

export default function RequestTypes(): ReactNode {
    const { data, error, loading: isLoading } = useGetRequestTypesQuery({
        variables: {
            type: FormType.Default,
        },
    });

    const { historyPush } = useRouting();

    const [cloneForm] = useCloneFormMutation();
    const [deleteForm] = useDeleteFormMutation();
    const [saveWorkflow] = useSaveWorkflowMutation();
    const [saveForm] = useSaveFormMutation();
    const [attachWorkflow] = useAttachWorkflowMutation();

    const clone = (id: string) => {
        LoggingHandler.instance.wrap(async () => {
            await cloneForm({
                refetchQueries: [
                    { query: GetFormDataDocument },
                    { query: GetFormDocument, variables: { id } },
                    { query: GetRequestTypesDocument, variables: { type: FormType.Default } },
                ],
                variables: {
                    id,
                },
            });
        });
    };

    const requestTypes = useMemo(() => {
        const groupMap = data?.userGroups.toIdMap("id") ?? {};
        return data?.forms.map(form => ({
            ...form,
            groups: form.userGroupIds?.mapFilter(id => groupMap[id]) ?? [],
        })) ?? [];
    }, [data?.forms]);

    const { Paths, createHistoryPush } = useRouting();

    const { element: deleteElement, setDeleting } = useDeleteConfirm<Form>(
        async item => {
            await deleteForm({
                refetchQueries: [
                    { query: GetFormDataDocument },
                    { query: GetRequestTypesDocument, variables: { type: FormType.Default } },
                ],
                variables: {
                    id: item.id,
                },
            });
        },
        item => item.name,
        "Delete Request Type",
        "Once you delete this request type, users will no longer "
        + "be able to create tickets with this request type. Do you want to delete",
    );

    const templates = useMemo(() => data?.templates.map(template => {
        const JobIcon = template.jobFunctions?.isNotEmpty ? ROLE_MAP[template.jobFunctions?.firstElement]?.icon : undefined;
        return ({
            id: template.id,
            title: template.name,
            description: template.description,
            categories: template.jobFunctions?.mapFilter(jobFunction => ROLE_MAP[jobFunction]?.title),
            icon: JobIcon ? <JobIcon /> : undefined,
        } as TemplateItem);
    }) ?? [], [data?.templates]);

    const { element: templateElement, onShow: onShowTemplate } = useTemplate({
        templates,
        title: "Templates Library",
        categoryIcons: ICON_MAP,
        categoryIcon: <RiLayout6Line />,
        onUseTemplate: async template => {
            const created = await cloneForm({
                refetchQueries: [GetRequestTypesDocument],
                variables: {
                    id: template.id,
                },
            });
            historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                id: created.data?.cloneForm.id,
                setting: "request-forms",
            });
        },

    });

    const handleCreateRequestType = useCallback(async () => {
        const newWorkflow = await saveWorkflow({
            variables: {
                workflow: {
                    name: `New Request Type Automation`,
                    description: "",
                    steps: [],
                },
            },
        });

        const saved = await saveForm({
            variables: {
                form: {
                    name: DEFAULT_NAME,
                    type: FormType.Default,
                    description: DEFAULT_DESCRIPTION,
                    fields: [],
                    workflowId: newWorkflow.data?.saveWorkflow?.id,
                },
            },
        });

        const formId = saved.data?.saveForm?.id;
        const formName = saved.data?.saveForm?.name ?? "";

        await attachWorkflow({
            variables: {
                id: newWorkflow.data?.saveWorkflow?.id,
                triggers: [{
                    referenceId: formId,
                    text: formName,
                    type: WorkflowTriggerType.RequestType,
                }],
            },
        });

        createHistoryPush(Paths.ADMINISTRATOR_SETTING_ID, {
            setting: "request-forms",
            id: formId,
        });
    }, [createHistoryPush, Paths.ADMINISTRATOR_SETTING_ID]);

    return (
        <SettingsContainer.Section
            sectionKey="request-types"
            title="Request Types"
            // counter={(requestTypes.length ?? 0).toString()}
            shouldDisplayTitle={false}
        >
            <SettingsContainer.Setting
                title="Request Types"
                description="Request types allow you to define fields and logic
                    that a user must enter when a ticket is being created."
                permissions={SettingsContainer.Setting.Permissions.Administrator}
                isFullWidth
            >
                <TableContainer data={requestTypes as FormWithGroups[] ?? []} isLoading={isLoading} error={error}>
                    <TableContainer.Empty>
                        <TableContainer.Empty.Default
                            text="Request types allow you to define fields and logic
                                that a user must enter when a ticket is being created."
                            title="Create an Request Type"
                            icon={<RiFileList2Line />}
                            onClick={handleCreateRequestType}
                            buttonText="Add Request Type"
                        />
                    </TableContainer.Empty>
                    <TableContainer.TableControls>

                        <TableContainer.TableControls.Search
                            search={(query, item) => item.name?.toLocaleLowerCase().includes(query)
                                || item.description?.toLocaleLowerCase().includes(query)
                                || item.groups.some(group => group.name?.toLocaleLowerCase().includes(query))}
                        />
                        <TableContainer.TableControls.Seperator />

                        <TableContainer.TableControls.Filters />

                        <TableContainer.TableControls.Filter
                            defaultValue={[] as string[]}
                            deserialize={TableContainer.TableControls.Filter.Serializers.deserializeArray}
                            serialize={TableContainer.TableControls.Filter.Serializers.serializeArray}
                            filterKey="groups"
                            onFilter={(item, selected) => selected.isEmpty
                                || selected.union(item.userGroupIds as string[]).isNotEmpty}
                        >
                            {(currentValue, setValue) => (
                                <UserGroups
                                    isMulti
                                    icon={<GroupsList />}
                                    onSelected={setValue}
                                    value={currentValue}
                                    searchMinimum={5}
                                    shouldShowDropdown
                                    toggleText="User Groups"
                                />
                            )}

                        </TableContainer.TableControls.Filter>
                        <TableContainer.TableControls.SortBy />
                        <TableContainer.TableControls.Button
                            text="Templates"
                            onClick={onShowTemplate}
                            variant="outline-gray"
                            prefixIcon={<RiLayout6Line />}
                            size="small"
                        />
                        <TableContainer.TableControls.Create
                            text="Add Request Type"
                            size="small"
                            onClick={createHistoryPush(Paths.ADMINISTRATOR_SETTING_ID, {
                                setting: "request-forms",
                                id: "new",
                            })}
                        />
                    </TableContainer.TableControls>
                    <TableContainer.Table>
                        <TableContainer.TableValue
                            columnKey="name"
                            title="Request Type"
                            sortBy={item => item.name?.toLowerCase()}
                        >
                            {item => item.name}
                        </TableContainer.TableValue>
                        <TableContainer.TableValue
                            columnKey="description"
                            title="Description"
                            sortBy={item => item.description?.toLowerCase()}
                        >
                            {item => (item.description
                                ? <HtmlRenderer html={item.description} shouldTrimBottom stripFormatting="all" />
                                : ""
                            )}
                        </TableContainer.TableValue>
                        <TableContainer.TableValue
                            columnKey="automations"
                            title="Automations"
                            sortBy={item => item.hasAutomation}
                        >
                            {item => (item.hasAutomation ? (
                                <Badge
                                    variant="gray"
                                    prefixIcon={<RiFlashlightLine />}
                                />
                            ) : undefined)}
                        </TableContainer.TableValue>

                        <TableContainer.TableValue
                            columnKey="channelTags"
                            title="Channel Groups"
                            sortBy={item => item}
                        >
                            {item => item.groups?.map(group => (
                                <Badge variant="primary" className="m3e-1" key={group.id}>
                                    {group.name}
                                </Badge>
                            ))}
                        </TableContainer.TableValue>

                        <TableContainer.Action
                            icon={<RiEdit2Line />}
                            altText="Edit Request Type"
                            to={Paths.ADMINISTRATOR_SETTING_ID}
                            getLinkParams={item => ({
                                setting: "request-forms",
                                id: item.id,
                            })}
                        />
                        <TableContainer.Action
                            icon={<RiFileCopyLine />}
                            altText="Clone Request Type"
                            onClick={item => clone(item.id)}
                        />
                        <TableContainer.Action
                            icon={<RiDeleteBinLine />}
                            altText="Delete Request Type"
                            onClick={item => {
                                setDeleting(item);
                            }}
                        />

                    </TableContainer.Table>
                </TableContainer>
            </SettingsContainer.Setting>
            {deleteElement}
            {templateElement}
        </SettingsContainer.Section>
    );
}
